import type {Module} from "vuex";
import type {RootState} from "@/store/types";
import emitter from "@/api/events";
import {retryRequest} from "@/helpers/request";
import {order} from "@/api/order";
import {platform} from "@/api/platform";

// User as returned by GET /orders/users
export interface ImpersonationOrderUserResponseItem {
    _id: string
    [key: string]: unknown
}

export interface ImpersonationOrderUser extends ImpersonationOrderUserResponseItem {
    id: string
}

export interface PaginatedResult<T> {
    items: T[]
    total: number
}

export interface ImpersonationOrderItemPayload {
    userId: string
    orderItemId: string
}

export interface ImpersonationOrderCancelPayload extends ImpersonationOrderItemPayload {
    sendEmail?: boolean
}

interface PaginatedResponse<T> {
    results: T[]
    pagination: {totalCount: number}
}

const orders: Module<Record<string, never>, RootState> = {
    namespaced: true,
    actions: {
        loadUsers(context, params: Record<string, unknown>): Promise<PaginatedResult<ImpersonationOrderUser>> {

            return retryRequest(() => order.get<PaginatedResponse<ImpersonationOrderUserResponseItem>>("/orders/users", {params}))
                .then(response => ({
                    items: response.data.results.map(user => ({...user, id: user._id})),
                    total: response.data.pagination.totalCount
                }));
        },
        loadItems(context, params: {userId: string, [key: string]: unknown}): Promise<PaginatedResult<Record<string, unknown>>> {

            return retryRequest(() => order.get<PaginatedResponse<Record<string, unknown>>>(`/users/${params.userId}/items/impersonated`, {params}))
                .then(response => ({items: response.data.results, total: response.data.pagination.totalCount}));
        },
        // Order actions run with the impersonated user's token
        cancel(context, {userId, orderItemId, sendEmail}: ImpersonationOrderCancelPayload) {

            return platform
                .post<{results: {token: string}}>(`/api/v2/users/${this.getters["user/id"]}/impersonate`, {impersonate: userId})
                .then(response => order.put(
                    `/users/${userId}/items/${orderItemId}/cancel`,
                    {id: orderItemId, sendEmail},
                    {headers: {Authorization: response.data.results.token}}
                ))
                .then(() => emitter.emit('user_impersonation_order_cancel'));
        },
        received(context, {userId, orderItemId}: ImpersonationOrderItemPayload) {

            return platform
                .post<{results: {token: string}}>(`/api/v2/users/${this.getters["user/id"]}/impersonate`, {impersonate: userId})
                .then(response => order.put(
                    `/users/${userId}/items/${orderItemId}/received`,
                    {},
                    {headers: {Authorization: response.data.results.token}}
                ))
                .then(() => emitter.emit('user_impersonation_order_received'));
        },
        undo(context, {userId, orderItemId}: ImpersonationOrderItemPayload) {

            return platform
                .post<{results: {token: string}}>(`/api/v2/users/${this.getters["user/id"]}/impersonate`, {impersonate: userId})
                .then(response => order.post(
                    `/users/${userId}/items/${orderItemId}/status/undo`,
                    {},
                    {headers: {Authorization: response.data.results.token}}
                ))
                .then(() => emitter.emit('user_impersonation_order_undo'));
        }
    }
}

export default orders
